import { ListMagnifyingGlass, Plus } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { DealWithLocations } from '../../../../../types/apiTypes';
import { VendorBusinessLocation } from '../../../../../types/dbTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import PromotionsGrid from './widgets/PromotionsGrid';
import { Select } from 'antd';

interface VendorPromotionsListProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  businessLocations: VendorBusinessLocation[];
  loadingLocations: boolean;
}

function VendorPromotionsList({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  businessLocations,
  loadingLocations,
}: VendorPromotionsListProps) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const [loadingDeals, setLoadingDeals] = useState<boolean>(false);
  const [deals, setDeals] = useState<DealWithLocations[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<
    { label: string; value: string }[]
  >([
    {
      label: 'All Locations',
      value: 'All Locations',
    },
  ]);
  const [filteredDeals, setFilteredDeals] = useState<DealWithLocations[]>([]);
  const { callProtectedApi } = useApi();
  const { userId } = useUser();
  useEffect(() => {
    const fetchDealsForVendor = async (vendor_id: string) => {
      setLoadingDeals(true);
      try {
        const response = await callProtectedApi(
          `/deals/list/${vendor_id}`,
          'get',
          {},
          'Error occurred while fetching your promotions.'
        );
        const data = response?.data?.data?.deals;
        if (
          data &&
          data?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setDeals(data);
          setFilteredDeals(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching your promotions: ', e);
      } finally {
        setLoadingDeals(false);
      }
    };

    fetchDealsForVendor(userId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedLocations?.map((loc) => loc?.value)?.includes('All Locations')
    ) {
      setFilteredDeals(deals);
    } else if (selectedLocations?.length > 0) {
      const filteredDeals = deals?.filter((deal) => {
        return selectedLocations?.find((loc) =>
          deal?.businessLocations
            ?.map((bus) => bus?.location_id)
            ?.includes(loc?.value)
        );
      });
      setFilteredDeals(filteredDeals);
    } else {
      setFilteredDeals(deals);
    }
  }, [selectedLocations, deals]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return loadingLocations ||
          loadingDeals ||
          (deals && deals?.length > 0) ? (
          <div className="flex h-full flex-col items-center justify-center overflow-y-auto overflow-x-hidden sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px]">
            {/* <OnboardingModal /> */}
            {!loadingLocations && !loadingDeals ? (
              <div className="flex h-full w-full flex-col items-center justify-center space-y-[16px] pt-[16px]">
                <div className="flex w-full flex-row items-end justify-between space-x-2">
                  <LocationSelect
                    businessLocations={businessLocations}
                    selectedLocations={selectedLocations}
                    onChange={(
                      selectedLocs: { value: string; label: string }[]
                    ) => {
                      if (
                        selectedLocs?.length > 1 &&
                        selectedLocations?.length === 1 &&
                        selectedLocations[0]?.value === 'All Locations'
                      ) {
                        setSelectedLocations(
                          selectedLocs.filter(
                            (item) => item?.value !== 'All Locations'
                          )
                        );
                      } else if (
                        selectedLocations?.length > 0 &&
                        selectedLocs
                          ?.map((loc) => loc?.value)
                          ?.includes('All Locations')
                      ) {
                        setSelectedLocations(
                          selectedLocs.filter(
                            (loc) => loc?.value === 'All Locations'
                          )
                        );
                      } else {
                        setSelectedLocations(selectedLocs);
                      }
                    }}
                  />
                  <AccentButtonOutline
                    onClick={() => navigate('/dashboard/promotions/create')}
                    addClass="h-[48px] max-h-[48px] w-[196px] max-w-[196px] rounded-full"
                  >
                    <div className="flex h-full w-full flex-row items-center justify-center space-x-2 align-middle">
                      <Plus />
                      <p>Add Promotion</p>
                    </div>
                  </AccentButtonOutline>
                </div>
                <div className="flex h-full w-full flex-row">
                  <PromotionsGrid
                    promotions={filteredDeals}
                    loading={loadingLocations}
                  />
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingLocations || loadingDeals}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        ) : (
          <div className="mt-[16px] flex h-full w-[95%] flex-col items-center justify-center gap-y-6 bg-white p-8 align-middle lg:w-[1148px] lg:max-w-[1148px]">
            <div className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7]">
              <ListMagnifyingGlass
                size={40}
                className="text-graysecondary"
                weight="bold"
              />
            </div>
            <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
              <h2 className="text-[16px] text-primary">
                No Promotions to Display
              </h2>
              <p className="text-center text-[14px] text-graysecondary">
                Get started by adding your first promotion.
              </p>
            </div>
            <AccentButtonOutline
              onClick={() => navigate('/dashboard/promotions/create')}
              addClass="rounded-full text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
            >
              <Plus size={20} />
              <h2 className="text-[14px]">Add Promotion</h2>
            </AccentButtonOutline>
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default VendorPromotionsList;

interface LocationSelectProps {
  businessLocations: VendorBusinessLocation[];
  onChange: (value: { value: string; label: string }[]) => void;
  selectedLocations: { value: string; label: string }[];
}

const LocationSelect = ({
  businessLocations,
  onChange,
  selectedLocations,
}: LocationSelectProps) => {
  const options = [
    {
      label: 'All Locations',
      value: 'All Locations',
    },
    ...businessLocations.map((loc: VendorBusinessLocation) => ({
      label: `${loc.address_st_number} ${loc.address_st_name} ${loc.address_unit ?? ''}, ${loc.address_city}, ${loc.address_state} ${loc.address_zip}`,
      value: loc.location_id,
    })),
  ];
  return (
    <Select
      mode="multiple"
      allowClear
      defaultValue={[{ label: 'All Locations', value: 'All Locations' }]}
      placeholder="Select locations"
      className="z-40 h-[34px] w-[382px]"
      maxTagCount={1} // Show only 2 tags
      value={selectedLocations}
      maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
      onChange={onChange}
      options={options}
      labelInValue={true}
    />
  );
};
